import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays, MapPin, Waves, Users, Backpack, Sparkles } from "lucide-react";
import heroImage from "@/assets/gallery/kayaking-friends.jpg";
import tubingImg from "@/assets/tubing/family-tubing-fun.jpg";
import cabanaImg from "@/assets/cabanas/cabana-riverside.jpg";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { faqPageSchema, breadcrumbSchema } from "@/lib/schema";

const PUBLISH_DATE = "May 27, 2026";
const CANONICAL = "/blog/kayaking-guadalupe-river-new-braunfels";
const TITLE = "Kayaking the Guadalupe River Near New Braunfels — Routes, Rentals & Tips | Son's";
const DESCRIPTION =
  "A family-friendly guide to kayaking the Guadalupe River near New Braunfels — best put-in spots, river flow, what to bring, kid tips, and how to pair a paddle day with a private cabana at Son's.";

const FAQS = [
  {
    q: "Is the Guadalupe River good for kayaking?",
    a: "Yes — the stretch of the Guadalupe near New Braunfels is one of the best beginner-to-intermediate kayaking rivers in Texas. Spring-fed flow, clear water, mild Class I rapids, and plenty of calm pools make it ideal for families and casual paddlers.",
  },
  {
    q: "Do I need experience to kayak the Guadalupe?",
    a: "No prior experience required for the New Braunfels stretch. Most floats are flatwater with a few light riffles. If you can sit upright and paddle in a straight line, you'll have a great day. Stronger flows above 300 CFS are better for experienced paddlers.",
  },
  {
    q: "Where can I put in a kayak on the Guadalupe?",
    a: "Popular put-ins along River Road include the Horseshoe Loop area, 4th Crossing, and Hueco Springs. Many river outfitters and private properties (including Son's Guadalupe) offer kayak-friendly river access with parking.",
  },
  {
    q: "Can kids kayak the Guadalupe?",
    a: "Yes — kids 6+ can paddle a tandem kayak with an adult on calm stretches. Younger kids should ride along in tandem boats and wear properly-sized life jackets. Avoid kayaking with kids during high flow (above 400 CFS).",
  },
  {
    q: "How long does it take to kayak the Guadalupe?",
    a: "Most popular runs are 2–4 miles and take 1.5–3 hours depending on flow. The classic Horseshoe Loop is about 3 hours. Faster, longer runs are possible for experienced paddlers willing to shuttle.",
  },
  {
    q: "Can I kayak and tube the same day at Son's?",
    a: "Absolutely. Many Son's guests paddle in the morning when the river is quietest, then switch to tubing the same afternoon — your cabana is the basecamp for both. We offer tubing through Son's Guadalupe; kayak rentals are available at nearby outfitters along River Road.",
  },
  {
    q: "What should I wear kayaking the Guadalupe?",
    a: "Quick-dry shorts, swimsuit, water shoes or sandals with a heel strap, sun shirt, and a hat with a strap. The water is spring-fed and cool year-round (high 70s in summer). Always wear a Coast Guard-approved life jacket.",
  },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Kayaking the Guadalupe River Near New Braunfels — Routes, Rentals & Tips",
    description: DESCRIPTION,
    image: "https://sonsguadalupe.com/og-image.png",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: { "@type": "ImageObject", url: "https://sonsguadalupe.com/og-image.png" },
    },
    datePublished: "2026-05-27",
    dateModified: "2026-05-27",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
    keywords:
      "kayaking guadalupe river, kayak rental new braunfels, kayak guadalupe river road, family kayaking texas, guadalupe river paddling",
  },
  faqPageSchema(FAQS),
  breadcrumbSchema([
    { name: "Home", url: "https://sonsguadalupe.com/" },
    { name: "Blog", url: "https://sonsguadalupe.com/blog" },
    { name: "Kayaking the Guadalupe River", url: `https://sonsguadalupe.com${CANONICAL}` },
  ]),
];

const PUT_INS = [
  {
    name: "Horseshoe Loop",
    distance: "~3 miles",
    time: "~2.5 hours",
    skill: "Beginner",
    notes: "The classic loop float. Calm pools, a few light riffles, and easy access from River Road. Best for families and first-time paddlers.",
  },
  {
    name: "4th Crossing to 1st Crossing",
    distance: "~4 miles",
    time: "~3 hours",
    skill: "Beginner–Intermediate",
    notes: "A longer scenic run with cypress-shaded bends. Manageable Class I riffles. Shuttle required.",
  },
  {
    name: "Hueco Springs to Horseshoe",
    distance: "~5 miles",
    time: "~3.5 hours",
    skill: "Intermediate",
    notes: "Faster current, a few rocky stretches. Skip during high flow (>400 CFS) with kids.",
  },
];

const BlogKayakingGuadalupe = () => {
  const related = getRelatedPosts("kayaking-guadalupe-river-new-braunfels", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async"
            src={heroImage}
            alt="Friends kayaking the spring-fed Guadalupe River near New Braunfels, Texas"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Kayaking the Guadalupe River Near New Braunfels — Routes, Rentals & Tips
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Tubing gets all the headlines, but <strong>kayaking the Guadalupe River near New Braunfels</strong> is the
                Texas Hill Country's best-kept paddling secret. Spring-fed clear water, easy Class I floats, cypress-shaded
                bends, and plenty of public put-ins along River Road. Here's everything you need to plan a great day on
                the water — and how to pair it with a private cabana at Son's.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2">
                <Waves className="w-5 h-5 text-teal" /> Why Kayak the Guadalupe (vs. Tube)?
              </h2>
              <p>
                Tubing is social, slow, and floaty. Kayaking is faster, quieter, and gets you into stretches you can't
                reach in a tube. The Guadalupe's spring-fed flow stays cool and clear year-round, so kayaking works
                spring through fall — not just summer. If you want a paddle workout, want to fish, or just want a calm
                morning before the river fills up, kayak. Want the lazy afternoon with a cooler and friends? Tube.
                Most Son's guests do both in the same day.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal" /> Best Put-Ins & Routes
              </h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left p-3 border-b">Route</th>
                      <th className="text-left p-3 border-b">Distance</th>
                      <th className="text-left p-3 border-b">Time</th>
                      <th className="text-left p-3 border-b">Skill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PUT_INS.map((r) => (
                      <tr key={r.name} className="border-b align-top">
                        <td className="p-3 font-semibold">{r.name}</td>
                        <td className="p-3">{r.distance}</td>
                        <td className="p-3">{r.time}</td>
                        <td className="p-3">{r.skill}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                {PUT_INS.map((r) => (
                  <li key={r.name}>
                    <strong>{r.name}:</strong> {r.notes}
                  </li>
                ))}
              </ul>

              <img
                src={cabanaImg}
                alt="Riverside cabana basecamp on the Guadalupe — perfect for a kayak-and-tube day"
                loading="lazy"
                className="w-full rounded-2xl shadow-elevated my-8"
              />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal" /> River Flow & Skill Level
              </h2>
              <p>
                The Guadalupe near New Braunfels is dam-controlled below Canyon Lake, so flow is reliable. Check the USGS
                Sattler gauge before you go.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>100–200 CFS:</strong> Mellow. Great for kids and first-timers. Some shallow scraping.</li>
                <li><strong>200–400 CFS:</strong> The sweet spot. Fun riffles, easy paddling, good for everyone.</li>
                <li><strong>400–800 CFS:</strong> Faster, more pushy water. Experienced paddlers only with kids.</li>
                <li><strong>800+ CFS:</strong> Skip it — wait for flow to come down.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2">
                <Backpack className="w-5 h-5 text-teal" /> What to Bring
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Coast Guard-approved life jacket (required for every paddler)</li>
                <li>Quick-dry shorts and a sun shirt — no cotton</li>
                <li>Water shoes or sandals with a heel strap (Tevas, Chacos)</li>
                <li>Hat with a strap, polarized sunglasses with retainer</li>
                <li>Reef-safe sunscreen</li>
                <li>Dry bag for phone, keys, snacks</li>
                <li>Plenty of water — at least 1L per paddler</li>
                <li>Small first-aid kit and a whistle</li>
              </ul>

              <img
                src={tubingImg}
                alt="Family kayaking and tubing on the Guadalupe River near New Braunfels"
                loading="lazy"
                className="w-full rounded-2xl shadow-elevated my-8"
              />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2">
                <Users className="w-5 h-5 text-teal" /> Family & Kid Tips
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use a tandem (2-person) kayak with kids under 10 — they can paddle as much or as little as they want.</li>
                <li>Life jackets must fit properly — too loose pulls up over the ears in water.</li>
                <li>Start at Horseshoe Loop. It's the gentlest, prettiest, and most forgiving run.</li>
                <li>Go in the morning. The river is quietest, the air is cool, and the light on the water is unreal.</li>
                <li>Plan a cabana for the afternoon — kids will be tired, hot, and hungry. A shaded basecamp with a grill solves all three.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Make It a Full Day at Son's</h2>
              <p>
                The smartest Guadalupe day: kayak in the morning while the river is quiet, then come back to your{" "}
                <Link to="/cabanas" className="text-primary hover:underline">private cabana at Son's</Link> for lunch and an
                afternoon of <Link to="/tubing" className="text-primary hover:underline">tubing</Link>. Your cabana has a
                charcoal grill, picnic table, shaded seating, and dedicated parking — and the float ends right at your
                cabana, no shuttle scramble. Stay overnight at our <Link to="/glamping" className="text-primary hover:underline">AC-cooled glamping tents</Link>{" "}
                and you'll have the river practically to yourself before 10:30 AM and after 6:30 PM.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Kayaking the Guadalupe FAQ</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Your Cabana</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Tubing</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />
      </main>
      <SEOContentBlock
        eyebrow="Plan Your Paddle Day"
        heading="Kayaking, Tubing & Cabanas on the Guadalupe River"
        paragraphs={[
          "Kayaking the Guadalupe River near New Braunfels is the perfect morning before an afternoon of tubing. Pair your paddle with a private cabana on the Guadalupe River, an AC-cooled glamping tent near New Braunfels, or overnight camping along River Road — and turn a paddle day into a full Hill Country weekend.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogKayakingGuadalupe;
